import { VoucherWelcomePreviewResponse } from '@core/data-access/models/exhibitor/exhibitor-api.model';
import { BookingProductType } from '@products/models/booking.model';
import { PromoCodes } from '@products/models/smart-promotions';
import { UserAccountTariffSpecificLimits } from '@products/models/tariff-status.model';
import { TariffClassification } from '@products/models/tariff.model';

export const enum VoucherWarningMessage {
  VOUCHER_REGULAR_AFTER_ANONYMOUS_TICKET_WARNING = 'ticket-selection.regular-ticket-after-anonymous-ticket-message',
  VOUCHER_ANONYMOUS_PRICED_TICKET_WARNING = 'ticket-selection.anonymous-priced-ticket-message'
}

export const enum PromoCodeWarningMessage {
  INVALID_PROMO_CODE_WARNING = 'confirmation.promo-code.invalid-promo-codes-message',
  NOT_COMBINABLE_PROMO_CODES_WARNING = 'voucher.promo-code.error-message.not-combinable'
}

export const enum VoucherType {
  LimiterPromoCode = 'limitedPromoCode',
  PromoCode = 'promoCode',
  OneTimeVoucher = 'oneTimeVoucher'
}

export type VoucherCode = string;

export interface VoucherSponsor {
  name: string;
  logo: string;
  link: string;
  eventBanner?: string;
  sponsorBanner?: string;
  styles?: {
    ThemeColor: string;
    ThemeTxColor: string;
    ProductCounterButtonTxColor: string;
    ProductCounterButtonBgColor: string;
    ProductCounterButtonBorderColor: string;
    ProductCounterSpinnerColor: string;
    AreaBlockBgActive: string;
  };
}

export interface VoucherContingentState {
  hasDaySellLimit: boolean;
  durationInDays: number;
  validFrom: string;
  validTill: string;
  shouldCalendarBeDisplayed: boolean;
}

export interface VoucherTypeState {
  initialNumberOfAvailableTariffs: number;
  isMandatory: boolean;
  isAnonymous: boolean;
  isAnonymousWithPrice: boolean;
}

export interface VoucherState {
  voucherCode: VoucherCode;
  isLoading: boolean;
  isProductSelection: boolean;
  isSelfRegistration: boolean;
  hasSponsorArea: boolean;
  isPromoCodeEnabled: boolean;
  checkPromoCodeDiscount: boolean;
  barcode: string;
  voucherTypingActive: boolean;
  isConfirmRedeemVoucherModalOpen: boolean;
}

export interface VoucherValidationState {
  maxTicketLimit: number;
  limitPromoCodes: number;
  limitLimitedPromoCodes: number;
  ticketLimitPerUserAccount: number;
  currentUserAccountTariffLimit: number;
  currentUserAccountTariffSpecificLimits: UserAccountTariffSpecificLimits;
  isVoucherIncludedPerUserAccountLimit: boolean;
  bookedProductsCount: number;
  isRedeemedAnonymousVoucherProductInProductSelectionList: boolean;
  isCurrentVoucherTypeMandatory: boolean;
  isCurrentVoucherTypeAnonymous: boolean;
}

export interface VoucherWarningMessageState {
  ticketLimitPerUserAccount: number;
  ticketLimitWarningMessage: string;
  voucherWarningMessage: string;
  invalidPromoCodeWarningMessage: string;
  invalidPromoCodes: string;
  notCombinablePromoCodesWarningMessage: string;
  notCombinablePromoCodes: string;
}

export interface VoucherDetail {
  eventId: number;
  ticketTypeId: number;
  ticketPersonTypeId: number;
  ticketPersonId: number;
  voucherCode: VoucherCode;
  voucherType: VoucherType;
  isValidVoucher: boolean;
  isValidPromoCode: boolean;
  isCombinable: boolean;
  notCombinableWith: PromoCodes;
}

export interface GetVoucherDetail {
  eventId: number;
  voucherCode: VoucherCode;
  confirmedPromoCodes: PromoCodes;
}

/*******************
 * VOUCHER RESPONSE
 *******************/
export interface VoucherDetailResponse {
  groupId: number;
  ticketPersonId: number;
  ticketTypeId: number;
  voucherCode: VoucherCode;
  voucherType: VoucherType;
  isValidPromoCode: boolean;
  isValidVoucher: boolean;
  isCombinable: boolean;
  notCombinableWith: PromoCodes;
  voucherWelcomePreview: VoucherWelcomePreviewResponse;
}

export interface RedeemVoucherResponse {
  allowedWorkshops: number[];
  allowedWorkshopsFull: boolean;
  availableTickets: number;
  classification: TariffClassification;
  groupId: number;
  groupName: string;
  info: string;
  infoExpanded: string;
  price: number;
  quantity: number;
  releasedInMinutes: number;
  sponsors: VoucherSponsor[];
  ticketCount: number;
  ticketLimit?: number;
  ticketName: string;
  ticketPersonId: number;
  ticketTypeId: number;
  ticketTypeOrder: number;
  voucherCode: VoucherCode;
  voucherType: VoucherType;
}

export interface RedeemedVoucher extends RedeemVoucherResponse {
  isAnonymous: boolean;
}

/******************
 * ACTION PAYLOADS
 ******************/
export interface BaseVoucher {
  eventId: number;
  orderUuid: string;
  voucherCode: VoucherCode;
  countryCode: string;
}

export interface RedeemVoucher extends BaseVoucher {
  isAnonymous: boolean;
  totalTicketCount: number;
}

export interface ReleaseVoucher extends BaseVoucher {
  ticketPersonId: number;
  remainingTicketCount: number;
}

export interface RemoveVoucher {
  eventId: number;
  orderUuid: string;
  ticketPersonId: number;
  voucherCode: VoucherCode;
}

export interface RemoveVoucherTariffBooking {
  ticketPersonId: number;
  voucherCode: VoucherCode;
}

export interface SetVoucher {
  redeemVoucher: RedeemVoucher;
  releaseVouchers: ReleaseVoucher[];
  removeProductBookings: BookingProductType[];
}

export interface SetRedeemedVoucher {
  redeemedVoucher: RedeemedVoucher;
  releaseVouchers: ReleaseVoucher[];
  removeProductBookings: BookingProductType[];
}
